// @ai-generated 2026-07-20T14:22:45Z by malte.ulfers@tngtech.com using Claude Opus 4.7 (1M context)
import { useAgentClient } from '@Hooks/AgenticOrchestrator/useAgentClient';
import { useRefreshChatThreads } from '@Hooks/AgenticOrchestrator/useChatThreads';
import { useChatTranscript } from '@Hooks/AgenticOrchestrator/useChatTranscript';
import { Button, FlexColumn, FrameHeader, LoadingFrame } from '@synqo/frontend-component-library';
import { CopilotChatView } from '@copilotkit/react-core/v2';
import '@copilotkit/react-core/v2/styles.css';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import { useChatAgent } from '@Hooks/AgenticOrchestrator/useChatAgent';
import { ChatEntityToolCallCards } from './ChatEntityToolCallCard';
import { createMessageView } from './ChatMessageList';
import { ChatPlainToolCallCards } from './ChatPlainToolCallCard';
import { ChatThreadRail } from './ChatThreadRail';
import { ChatToolCallCard } from './ChatToolCallCard';
import { ChatValidationToolCallCards } from './ChatValidationToolCallCard';
import { ChatWorkflowToolCallCards } from './ChatWorkflowToolCallCard';
import classes from './FrameAgentChat.module.css';
import { ToolCallSidebar } from './ToolCallSidebar';

// Specialized cards first; the wildcard card catches everything without one. Every command-based
// tool is named by one of these lists, so the wildcard is now only for tools we have not met.
const RENDER_TOOL_CALLS = [
    ...ChatEntityToolCallCards,
    ...ChatValidationToolCallCards,
    ...ChatPlainToolCallCards,
    ...ChatWorkflowToolCallCards,
    ChatToolCallCard,
];

const MessageView = createMessageView(RENDER_TOOL_CALLS);

function newThreadId() {
    return crypto.randomUUID().replace(/-/g, '');
}

/**
 * Agent chat page. The agent is a browser-side @ag-ui/client HttpAgent talking directly to our .NET
 * MapSynqoAgent endpoint, driven by {@link useChatAgent}.
 */
export function FrameAgentChat() {
    const { threadId: threadIdFromRoute } = useParams();
    const navigate = useClientNavigate();
    const [railCollapsed, setRailCollapsed] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

    const [fallbackThreadId] = useState(newThreadId);
    const threadId = threadIdFromRoute ?? fallbackThreadId;

    useEffect(() => {
        if (threadIdFromRoute === undefined) {
            navigate(`/chat/${fallbackThreadId}`, { replace: true });
        }
    }, [threadIdFromRoute, fallbackThreadId, navigate]);

    const { messages, error, refetch } = useChatTranscript(threadId);
    const refreshThreads = useRefreshChatThreads();

    const agent = useAgentClient(threadId);
    const { messages: shownMessages, isRunning, submit, stop } = useChatAgent(agent);

    useEffect(() => {
        if (messages === undefined) {
            return;
        }
        agent.threadId = threadId;
        agent.setMessages(messages);
    }, [agent, threadId, messages]);

    useEffect(() => {
        const subscription = agent.subscribe({ onRunFinishedEvent: () => void refreshThreads() });
        return () => subscription.unsubscribe();
    }, [agent, refreshThreads]);

    const openThread = (conversationId: string) => {
        navigate(`/chat/${conversationId}`);
    };

    const startNewThread = () => {
        openThread(newThreadId());
    };

    const onDeleted = (conversationId: string) => {
        if (conversationId === threadId) {
            openThread(newThreadId());
        }
    };

    return (
        <FlexColumn gap="none" height="100%" width="100%">
            <FrameHeader label="Agent-Chat" resourceLabel="Agent-Chat" />
            <div className={classes.chatRow}>
                <ChatThreadRail
                    activeConversationId={threadId}
                    collapsed={railCollapsed}
                    onToggleCollapsed={() => setRailCollapsed((collapsed) => !collapsed)}
                    onSelect={openThread}
                    onNew={startNewThread}
                    onDeleted={onDeleted}
                />
                {messages === undefined ? (
                    <div className={classes.chatColumn}>
                        {error ? (
                            <div className={classes.transcriptError}>
                                <span>Der bisherige Verlauf konnte nicht geladen werden.</span>
                                <Button onClick={() => void refetch()}>Erneut versuchen</Button>
                            </div>
                        ) : (
                            <LoadingFrame />
                        )}
                    </div>
                ) : (
                    <>
                        <div className={classes.chatColumn}>
                            <CopilotChatView
                                messages={shownMessages}
                                isRunning={isRunning}
                                onSubmitMessage={submit}
                                onStop={stop}
                                messageView={MessageView}
                                welcomeScreen={false}
                            />
                        </div>
                        <ToolCallSidebar
                            messages={shownMessages}
                            collapsed={sidebarCollapsed}
                            onToggleCollapsed={() => setSidebarCollapsed((collapsed) => !collapsed)}
                        />
                    </>
                )}
            </div>
        </FlexColumn>
    );
}
